import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Heading,
  useToast,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  Link,
  Select,
  Button,
  Flex,
  Stack,
  useDisclosure,
  useBreakpointValue,
} from "@chakra-ui/react";
import axios from "axios";
import Modal_guardian_sidebar from "./Modal_guardian_sidebar";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const Busfee_Guardian_Sidebar = () => {
  const [paidFees, setPaidFees] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("All");
  const toast = useToast();
  const studentId = sessionStorage.getItem("student_id");
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [transportInfo, setTransportInfo] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [students, setStudents] = useState([]);

  const userId = sessionStorage.getItem("user_id");

  useEffect(() => {
    const fetchGuardianData = async () => {
      try {
        const response = await fetch(
          `${BASE_URL}/guardian/guardian_profile.php?user_id=${userId}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          }
        );

        const data = await response.json();

        if (data.success) {
          // Ensure students is an array and extract all student IDs
          const studentIds = data.students.map((student) => student.student_id);
          setStudents(studentIds);
          console.log("data_guardian prof", studentIds);
        } else {
          setError(data.message);
        }
      } catch (err) {
        setError("Failed to fetch data. Please try again.");
      }
    };

    fetchGuardianData();
  }, [userId]);

  useEffect(() => {
    if (students.length) {
      fetchPaidFees();
    }
  }, [students, paymentStatusFilter]);

  const fetchPaidFees = async () => {
    setIsLoading(true);
    setError(null);

    console.log("Starting to fetch paid fees...");

    try {
      const allFeeDetails = [];
      console.log("Initializing empty array to store fee details...");

      // Process each student one after the other using a for...of loop
      for (const studentId of students) {
        console.log(`Processing request for student ID: ${studentId}`);

        try {
          const res = await axios.post(`${BASE_URL}/student/bus/bus_fee_details.php`,
            {
              student_id: studentId,
              school_id: sessionStorage.getItem("school_id"),
            }
          );

          console.log(`Response for student ID ${studentId}:`, res.data);

          if (res.data.success && res.data.data) {
            // Ensure student_id is treated as a string for comparison
            const filteredData = res.data.data.filter(
              (fee) => String(fee.student_id) === String(studentId) // Ensure both are strings
            );

            console.log(
              `Filtered data for student ${studentId}:`,
              filteredData
            );

            // Add filtered fee data to allFeeDetails
            if (filteredData.length > 0) {
              allFeeDetails.push(...filteredData);
            } else {
              console.log(`No fee data found for student ID ${studentId}`);
            }
          } else {
            console.log(`No fee data available for student ID ${studentId}`);
          }
        } catch (error) {
          console.log(
            `Error occurred while fetching fees for student ID ${studentId}:`,
            error
          );
          setError(`Failed to fetch paid fees for student ID ${studentId}.`);
          toast({
            title: "Error",
            description: `Failed to fetch paid fees for student ID ${studentId}.`,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
          break; // Exit the loop if an error occurs for a specific student
        }
      }

      console.log("All processed fee details:", allFeeDetails);

      if (allFeeDetails.length > 0) {
        const allFees = allFeeDetails.map((fee) => ({
          ...fee,
          month: new Date(fee.start_date).toLocaleString("default", {
            month: "long",
            year: "numeric",
          }),
        }));

        console.log("Mapped all fees with formatted month:", allFees);

        // Apply payment status filter
        const filteredFees = allFees.filter((fee) => {
          console.log(
            `Checking payment status for fee ID ${fee.fee_id}:`,
            fee.payment_status
          );
          if (paymentStatusFilter === "Paid") {
            return fee.payment_status === "Paid";
          } else if (paymentStatusFilter === "Unpaid") {
            return fee.payment_status === "Unpaid";
          }
          return true;
        });

        console.log("Filtered fees based on payment status:", filteredFees);

        // Sort unpaid fees first
        const sortedFees = filteredFees.sort((a, b) => {
          console.log(
            `Comparing fee statuses: ${a.payment_status} vs ${b.payment_status}`
          );
          if (a.payment_status === "Unpaid" && b.payment_status !== "Unpaid")
            return -1;
          if (a.payment_status !== "Unpaid" && b.payment_status === "Unpaid")
            return 1;
          return 0;
        });

        console.log("Sorted fees:", sortedFees);

        setPaidFees(sortedFees);
      } else {
        setError("No fee details found for the given students.");
        console.log("Error: No fee details found for the given students.");
        toast({
          title: "Error",
          description: "No fee details found for the given students.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.log("Error occurred while fetching paid fees:", err);
      setError("Failed to fetch paid fees.");
      toast({
        title: "Error",
        description: "Failed to fetch paid fees.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
      console.log("Loading state set to false, finished processing.");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const totalPages = Math.ceil(paidFees.length / rowsPerPage);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const paginatedFees = paidFees.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minH="200px"
      >
        <Spinner size="xl" color="blue.500" />
      </Box>
    );
  }

  // if (error) {
  //   return (
  //     <Alert status="error" borderRadius="md">
  //       <AlertIcon />
  //       {error}
  //     </Alert>
  //   );
  // }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  return (
    <Box w="100%">
      <div className="teacher-list-container">
        <div
          className="header"
          style={{ display: "flex", alignItems: "center" }}
        >
          <h1 style={{ fontSize: "20px", margin: 0 }}>Guardian Bus Fee</h1>
        </div>

        <Box className="breadcrumb">
          <Link
            to="/admin"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <span>Dashboard</span>
          </Link>{" "}
          / <span>Fee </span> /{" "}
          <Link
            to="/class/classlist"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <span>Guardian Bus Fee</span>
          </Link>
        </Box>
      </div>

      <Box p={6} flex="2" mb={1}>
        {paidFees.length === 0 ? (
          <Alert status="info">
            <AlertIcon />
            No fees found for this student.
          </Alert>
        ) : (
          <Box>
            <Flex
              direction={isMobile ? "column" : "row"}
              justify={isMobile ? "start" : "space-between"}
              align="center"
              className="sb"
              p={3}
            >
              <Box mb={isMobile ? 4 : 0} width={isMobile ? "100%" : "auto"}>
                <Flex align="center" width="100%">
                  {/* <Text mr={2}>Rows Per Page</Text> */}
                  <Select
                    width={isMobile ? "100%" : "100px"}
                    value={rowsPerPage}
                    onChange={handleRowsPerPageChange}
                    borderColor="#E9EDF4"
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </Select>
                </Flex>
              </Box>
              <div
                className="action-buttons"
                style={{
                  marginLeft: isMobile ? "0" : "auto",
                  width: isMobile ? "100%" : "auto",
                }}
              >
                <Select
                  value={paymentStatusFilter}
                  onChange={(e) => setPaymentStatusFilter(e.target.value)}
                  borderColor="#E9EDF4"
                  width={isMobile ? "100%" : "150px"}
                >
                  <option value="All">All</option>
                  <option value="Paid">Paid</option>
                  <option value="Unpaid">Unpaid</option>
                </Select>
              </div>
            </Flex>

            <Box overflowX="auto">
              <Table
                sx={{
                  "tbody tr:nth-of-type(even)": { backgroundColor: "#f2f4f8" },
                }}
              >
                <Thead>
                  <Tr>
                    <Th>Month</Th>
                    <Th>Payment Date</Th>
                    <Th>Due Date</Th>
                    <Th>Amount</Th>
                    <Th>Fine</Th>
                    <Th>Payment Method</Th>
                    <Th>Action</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedFees.map((fee) => (
                    <Tr key={fee.fee_id}>
                      <Td>
                        <Text fontWeight="medium">{fee.month}</Text>
                        {fee.description && (
                          <Text fontSize="sm" color="gray.600">
                            {fee.description}
                          </Text>
                        )}
                      </Td>
                      <Td>{formatDate(fee.payment_date)}</Td>
                      <Td>{formatDate(fee.due_date)}</Td>
                      <Td>{formatAmount(fee.bus_fee)}</Td>
                      <Td>
                        {fee.fine_amount > 0 ? (
                          <Badge colorScheme="red">
                            {formatAmount(fee.fine_amount)}
                          </Badge>
                        ) : (
                          "-"
                        )}
                      </Td>
                      <Td>
                        <Badge colorScheme="green" borderRadius="full">
                          {fee.payment_method === 2
                            ? "Online"
                            : fee.payment_method === 1
                            ? "Cash"
                            : "Not Paid"}
                        </Badge>
                      </Td>
                      <Td>
                        {fee.payment_status === "Unpaid" && (
                          <Button
                            size="sm"
                            colorScheme="teal"
                            onClick={() => {
                              setTransportInfo(fee);
                              onOpen();
                            }}
                          >
                            Pay
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
            <Flex justify="end" className="sb2" borderTopWidth="1px" p={3}>
              <Button
                onClick={() => handlePageChange(currentPage - 1)}
                isDisabled={currentPage === 1}
              >
                Prev
              </Button>
              <Stack direction="row" mx={2}>
                {currentPage > 1 && (
                  <>
                    <Button onClick={() => handlePageChange(1)}>1</Button>
                    {currentPage > 2 && <Text>...</Text>}
                  </>
                )}
                <Button backgroundColor="teal" color="white">
                  {currentPage}
                </Button>
                {currentPage < totalPages - 1 && (
                  <>
                    <Text>...</Text>
                    <Button onClick={() => handlePageChange(totalPages)}>
                      {totalPages}
                    </Button>
                  </>
                )}
              </Stack>
              <Button
                onClick={() => handlePageChange(currentPage + 1)}
                isDisabled={currentPage === totalPages}
              >
                Next
              </Button>
            </Flex>
          </Box>
        )}
      </Box>
      {isOpen && (
        <Modal_guardian_sidebar
          isOpen={isOpen}
          onClose={onClose}
          amount={transportInfo.bus_fee}
          last={transportInfo.due_date}
          start={transportInfo.start_date}
          feeId={transportInfo.id}
          studentId={transportInfo.student_id}
          userId={transportInfo.user_id}
        />
      )}
    </Box>
  );
};

export default Busfee_Guardian_Sidebar;
